import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useDialogStore } from '@/store/store';
import { useCheckPrems } from '@/utils/premissions/checkPrems';
import Premission from '@/utils/premissions/premission.type';

import 'dayjs/locale/ar';

dayjs.extend(relativeTime);
dayjs.locale('ar');

type Props = {
  id?: string;
  avatar?: string;
  empName: string;
  jobName?: string;
  size?: number;
  isDate?: Date;
  variant?: 'rounded' | 'circular' | 'square';
};

function AvatarToolTip({
  avatar,
  empName,
  id,
  jobName,
  size = 30,
  isDate,
  variant = 'rounded',
}: Props) {
  const { openUserDialog } = useDialogStore();
  const openProfilePrem = useCheckPrems(Premission?.Prem_Employee_Get_ONE);

  const handleClick = () => {
    if (openProfilePrem && id) {
      openUserDialog(id);
    }
  };

  // Determine the source for the avatar image
  const avatarSrc = avatar
    ? `${import.meta.env.VITE_HEALTHY_EMPIRE_SERVER}/public/avatar/thumb/${avatar}`
    : '/images/no-user.jpg';

  // Format date for readable time
  const formattedTime = isDate ? `منذ حوالي ${dayjs(isDate).fromNow()}` : null;

  // Compute the appropriate border-radius class based on variant
  const variantClass =
    variant === 'circular'
      ? 'rounded-full ring-1 ring-border'
      : variant === 'square'
        ? 'rounded-none ring-1 ring-border'
        : 'rounded-md ring-1 ring-border';

  return (
    <TooltipProvider>
      <Tooltip delayDuration={200}>
        <TooltipTrigger asChild>
          <button className="cursor-pointer outline-none">
            <Avatar
              style={{ width: size, height: size }}
              className={`transition-all hover:ring-primary/50 ${variantClass}`}
            >
              <AvatarImage src={avatarSrc} alt={empName} />
              <AvatarFallback className="bg-muted text-xs">
                {empName?.slice(0, 2) || 'غير معروف'}
              </AvatarFallback>
            </Avatar>
          </button>
        </TooltipTrigger>
        <TooltipContent
          side="bottom"
          align="center"
          className="w-[220px] overflow-hidden border border-border p-0"
          sideOffset={4}
        >
          <div className="flex items-center gap-2 p-2.5">
            {/* Left side - Avatar */}
            <Avatar className="h-10 w-10 shrink-0 border border-border">
              <AvatarImage
                src={avatarSrc}
                alt={empName}
                className="object-cover"
              />
              <AvatarFallback className="bg-muted text-xs">
                {empName?.slice(0, 2) || 'غير معروف'}
              </AvatarFallback>
            </Avatar>

            {/* Right side - Info */}
            <div className="min-w-0 flex-1">
              <h4 className="truncate text-xs font-medium">
                {empName || 'غير معروف'}
              </h4>

              <div className="mt-0.5 flex flex-wrap items-center gap-1">
                {jobName && (
                  <Badge
                    variant="outline"
                    className="h-4 border-border/70 px-1 text-[9px] font-normal text-muted-foreground"
                  >
                    {jobName}
                  </Badge>
                )}

                {isDate && formattedTime && (
                  <Badge
                    variant="outline"
                    className="h-5 w-full min-w-0 truncate bg-amber-100 px-1.5 py-px text-right text-[9px] font-medium text-amber-800 hover:bg-amber-100 dark:bg-amber-900/30 dark:text-amber-300"
                  >
                    {formattedTime}
                  </Badge>
                )}
              </div>
            </div>
          </div>

          {/* Profile button */}
          {openProfilePrem && id && (
            <div className="flex justify-end bg-muted/30 px-2 py-1.5">
              <Button
                variant="secondary"
                size="sm"
                className="h-6 rounded-sm px-3 text-[10px] font-medium transition-colors hover:bg-primary hover:text-primary-foreground"
                onClick={handleClick}
              >
                عرض الملف الشخصي
              </Button>
            </div>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export default AvatarToolTip;
